<template>
  <div class="customers-table w-100 i-mb-2">
    <div
      v-if="customers?.length === 0 && !isLoading"
      data-testid="customers-table-empty-state"
      class="empty-state d-flex flex-column justify-content-center align-items-center"
    >
      <empty-page
        icon="large_multiple_users_search_1"
        :title="emptyStateTitle"
        :message="emptyStateMessage"
        btn-text="New Customer"
        :btn-disabled="isUploadAccessBlocked"
        :btn-click="createNewCustomer"
      ></empty-page>
    </div>

    <div v-else>
      <!-- Only show loading state when there is no cached data -->
      <i-table
        data-testid="customers-table"
        :loading="isLoading"
        table-id="customers-table"
        ref="customersTable"
      >
        <!-- table header -->
        <template #header>
          <i-table-row type="header">
            <template #cells>
              <div class="no-padding-x not-hideable">Customer</div>

              <div>
                {{ unifiedAgentEnabled ? 'Rating' : 'Fraud' }}
              </div>

              <div
                v-if="canViewBusinessComplianceAgent && !unifiedAgentEnabled"
              >
                Compliance
              </div>
              <div>Status</div>
              <div>Documents</div>
              <div>
                <i-table-sort-button
                  current-order="desc"
                  label="Created"
                  class="sort-button no-padding-x"
                  @order-change="onSortChanged"
                ></i-table-sort-button>
              </div>
            </template>
          </i-table-row>
        </template>

        <!-- table body -->
        <template #rows>
          <i-table-row
            v-for="customer of customers"
            :key="customer.id"
            class="customer selectable"
            @on-row-click="handleRowClick(customer)"
          >
            <template #cells>
              <!-- name -->
              <div class="no-padding-x not-hideable">
                <icon-text
                  icon="medium-user-single"
                  :title="customer.name"
                  :subtitle="customer.creator.email"
                  :link="`customer/${customer.id}/overview`"
                  class="i-py-4"
                  data-testid="customer-name-link"
                  v-highlight-text="{
                    subElementTargetSelector: '.user-text a',
                    textToHighlight: searchQuery,
                  }"
                ></icon-text>
              </div>

              <!-- fraud rating , visible for unified agent otherwise mapping to risk level-->
              <div class="centered">
                <customer-fraud-risk-badge :customer="customer" />
              </div>

              <!-- compliance rating -->
              <div
                v-if="canViewBusinessComplianceAgent && !unifiedAgentEnabled"
                class="centered"
              >
                <customer-compliance-badge :customer="customer" />
              </div>

              <!-- status -->
              <div class="centered">
                <CustomerApprovalStatus
                  :approval-status="customer.approved"
                  class="centered"
                />
              </div>

              <!-- documents -->
              <div class="centered">
                <div
                  class="documents-summary d-flex flow-row align-items-center i-gap-1"
                >
                  <template
                    v-for="riskLevel in Object.keys(
                      CUSTOMER_SUMMARY_RISK_LEVELS,
                    )"
                  >
                    <i-button
                      v-if="
                        riskLevel !== RISK_LEVEL.NOT_APPLICABLE ||
                        customer.document_risk_counts?.[
                          CUSTOMER_SUMMARY_RISK_LEVELS[
                            RISK_LEVEL.NOT_APPLICABLE
                          ].key
                        ] > 0
                      "
                      :id="`total-id-${customer.id}-${riskLevel}`"
                      :button-style="iButtonStyles.GHOST"
                      :type="iButtonTypes.NEUTRAL"
                      :size="iButtonSizes.XSMALL"
                      :left-icon="CUSTOMER_SUMMARY_RISK_LEVELS[riskLevel].icon"
                      :label="
                        customer.document_risk_counts?.[
                          CUSTOMER_SUMMARY_RISK_LEVELS[riskLevel].key
                        ].toString() || '0'
                      "
                      class="button-count"
                      @button-click="handleRiskLevelClick(customer, riskLevel)"
                    >
                      {{ riskLevelToReadableLabel(riskLevel) }}
                      {{ riskLevel }}
                    </i-button>
                    <i-tooltip
                      :target="`#total-id-${customer.id}-${riskLevel}`"
                      :title="`${riskLevelToReadableLabel(riskLevel)}`"
                      :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
                    ></i-tooltip>
                  </template>
                </div>
              </div>

              <!-- created -->
              <div class="centered">
                <span
                  class="customer-created-at i-text-body-2 i-text-neutral-120"
                >
                  {{ formatDateTime(customer.created_at, 'lll') }}
                </span>
              </div>
            </template>
          </i-table-row>
        </template>
      </i-table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  iIcon,
  iBadgeColours,
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
  iTable,
  iTableRow,
  iTableSortButton,
  iTooltip,
  iTooltipAlignmentMode,
  iBadge,
  iSpinner,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import EmptyPage from '@/shared/components/EmptyPage';
import CustomerApprovalStatus from '@/V2/customers/CustomerApprovalStatus.vue';
import CustomerFraudRiskBadge from '@/V2/customers/fraud/CustomerFraudRiskBadge.vue';
import CustomerComplianceBadge from '@/V2/customers/business-compliance-agent/CustomerComplianceBadge.vue';
import { getAndSyncQueryParams, updateQueryParams } from '@/utils/queryParams';
import { formatDateTime } from '@/utils/date';
import IconText from '@/V2/components/IconText.vue';
import { readStorage } from '@/utils/storage';
import { RISK_LEVEL } from '@/constants/analyst-constants';
import { riskLevelToReadableLabel } from '@/constants/riskLevels';

const SELECTED_COLUMNS_STORAGE_KEY = 'customersTableColumns';

const CUSTOMER_SUMMARY_RISK_LEVELS = {
  [RISK_LEVEL.LOW]: {
    icon: 'small_solid_check',
    key: 'low',
  },
  [RISK_LEVEL.MEDIUM]: {
    icon: 'small_solid_alert_circle',
    key: 'medium',
  },
  [RISK_LEVEL.HIGH]: {
    icon: 'small_solid_alert_triangle',
    key: 'high',
  },
  [RISK_LEVEL.NOT_APPLICABLE]: {
    icon: 'small_solid_question',
    key: 'not_applicable',
  },
};

export default {
  components: {
    iButton,
    iIcon,
    iTable,
    iTableRow,
    iTableSortButton,
    iTooltip,
    IconText,
    EmptyPage,
    CustomerFraudRiskBadge,
    CustomerApprovalStatus,
    CustomerComplianceBadge,
    iBadge,
    iSpinner,
  },
  data() {
    return {
      CUSTOMER_SUMMARY_RISK_LEVELS,
      RISK_LEVEL,
      iBadgeColours,
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      iTooltipAlignmentMode,
      palette,
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      riskLevelToReadableLabel,
    };
  },
  computed: {
    ...mapGetters([
      'getCachedCustomers',
      'isUploadAccessBlocked',
      'canViewAIFraudAnalyst',
      'canViewBusinessComplianceAgent',
      'unifiedAgentEnabled',
    ]),
    searchQuery() {
      return this.$route.query.search || '';
    },
    customers() {
      return this.getCachedCustomers(this.params)?.data;
    },
    isLoading() {
      return typeof this.customers === 'undefined';
    },
    hasFiltersApplied() {
      return this.params && Object.keys(this.params).length > 0;
    },
    emptyStateTitle() {
      return this.hasFiltersApplied
        ? 'No customers match these filters'
        : "You haven't created any customers yet";
    },
    emptyStateMessage() {
      return this.hasFiltersApplied
        ? 'Matching customers will appear here'
        : 'All of your customers will appear here. Start by creating a new customer';
    },
    sortValue() {
      return this.params && this.params.order === '-created_at';
    },
    isFromAnalytics() {
      return this.$route.query.return_to === 'analytics';
    },
    fields() {
      if (this.canViewAIFraudAnalyst || this.canViewBusinessComplianceAgent) {
        return this.allFields.filter((field) => field.key !== 'fraud_rating');
      }
      return this.allFields;
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
      immediate: true,
    },
    customers() {
      this.manageTableSelectedColumns();
    },
  },
  methods: {
    formatDateTime,
    manageTableSelectedColumns() {
      const storedColumns = readStorage(SELECTED_COLUMNS_STORAGE_KEY);
      if (storedColumns) {
        this.$nextTick(() => {
          this.$refs.customersTable.setVisibleColumns(storedColumns);
        });
      }
    },
    handleRowClick(customer) {
      const routeData = {
        name: 'customer-overview',
        params: {
          customer_id: customer.id,
        },
      };

      // if the user is coming from analytics, we need to preserve the query params
      if (this.isFromAnalytics) {
        routeData.query = this.$route.query;
      }

      this.$router.push(routeData).catch(() => {});
    },
    createNewCustomer() {
      this.$router
        .push({
          name: 'new-customer',
        })
        .catch(() => {});
    },
    onSortChanged(value) {
      const sortValue = value === 'asc' ? '-created_at' : 'created_at';
      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          order: sortValue,
        },
      });
    },
    handleRiskLevelClick(customer, riskLevel) {
      this.$router.push({
        name: 'customer-documents',
        params: {
          customer_id: customer.id,
        },
        query: {
          risk_levels: riskLevel === RISK_LEVEL.NOT_APPLICABLE ? '' : riskLevel,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.customers-table {
  .empty-state {
    img {
      max-width: 200px;
    }
  }

  .documents-summary {
    .i-button {
      background: transparent;

      .i-icon {
        width: 12px !important;
        min-width: 12px !important;
        height: 12px !important;
        min-height: 12px !important;
      }

      &:hover {
        background: var(--surface-neutral);
      }
    }

    path {
      fill: var(--icon-neutral-disabled);
    }
    .label {
      color: var(--text-neutral-bold);
    }
  }

  .customers-table {
    color: $light-neutral-120;

    table {
      th {
        vertical-align: bottom !important;
      }
    }

    .icon-wrapper {
      background: $light-primary-30;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      flex-shrink: 0;
      .i-icon {
        color: $light-primary-110 !important;
      }
    }
    .customer-column {
      width: 50%;
      .customer-name {
        a {
          max-width: 60ch;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }

    .customer-created-at {
      white-space: nowrap;
      width: 160px;
    }

    .status-column,
    .docs-column {
      width: 135px;
    }

    .fraud-rating-column {
      width: 170px;
    }

    .status-column .i-badge,
    .fraud-rating-column .i-badge {
      display: inline-flex !important;
      white-space: nowrap;
    }
  }
}
</style>
